import { DashCircle } from "react-bootstrap-icons";
import * as styles from "./ArsListing.module.css";
import { ArsListingColumnDropSortVariableTarget } from "./ArsListingColumnDropSortVariableTarget";
import { useArsListing } from "./ArsListingContextProvider";
import { ARSL_COLUMN_UPDATE, ArsListingVariableEnum } from "./interface";

export interface ArsListingColumnVariablePropertiesProps {
  id: string;
}

/**
 * Column properties variable list item
 *
 * @param props - Component props
 * @param props.id - Variable ID in the column
 * @returns React node representing the variable properties
 */
export const ArsListingColumnVariableProperties = <T,>({
  id,
}: ArsListingColumnVariablePropertiesProps): React.ReactNode => {
  const { state, dispatch } = useArsListing<T>();
  if (!state?.settings.viewColumnPropertiesId) return null;
  const colId = state?.settings.viewColumnPropertiesId;
  const colIx = state.bodyData.arsListingColumns.findIndex((col) => col.id === colId);
  const column = colIx >= 0 ? state.bodyData.arsListingColumns[colIx] : undefined;
  const varIx = column?.variables.findIndex((v) => v.id === id) ?? -1;
  const variable = varIx >= 0 ? column?.variables[varIx] : undefined;

  if (!column || !variable) return null;

  /**
   * Handles the change of variable type in the select dropdown.
   *
   * @param e - Select change event
   */
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newVariableType = e.target.value as ArsListingVariableEnum;
    const newVariable = { ...variable, variableType: newVariableType };
    const newVariables = column?.variables.map((v) => (v.id === variable.id ? newVariable : v));
    dispatch({
      operation: ARSL_COLUMN_UPDATE,
      colId,
      column: { variables: newVariables },
    });
  };

  return (
    <div className={styles.variableProperties}>
      <ArsListingColumnDropSortVariableTarget
        colId={column.id}
        addVarIx={varIx}
      />
      <span className={styles.variablePropertiesLabel}>
        {variable.dataset}.{variable.variable}
      </span>
      <span className={styles.variablePropertiesTypeHolder}>
        <select
          className={styles.variablePropertiesTypeSelect}
          aria-label="Variable type"
          value={variable.variableType}
          onChange={handleChange}
        >
          <option
            value="displayOnly"
            className={styles.variablePropertiesTypeOption}
          >
            Display only
          </option>
          <option
            value="group"
            className={styles.variablePropertiesTypeOption}
          >
            Group
          </option>
          <option
            value="hidden-sort"
            className={styles.variablePropertiesTypeOption}
          >
            Sort
          </option>
        </select>
        <span
          className={styles.variablePropertiesDeleteButton}
          role="button"
          tabIndex={0}
          title="Delete variable"
          onClick={() => {
            const newVariables = column?.variables.filter((v) => v.id !== variable.id);
            dispatch({
              operation: ARSL_COLUMN_UPDATE,
              colId,
              column: { variables: newVariables },
            });
          }}
        >
          <DashCircle aria-label="Delete variable" />
        </span>
      </span>
    </div>
  );
};

ArsListingColumnVariableProperties.displayName = "ArsListingColumnVariableProperties";
